import { useSaveScalability, useScalability } from '@/api/queries'
import { ApiError } from '@/api/client'
import type { ScalabilityConfiguration, ServiceInfo } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/EmptyState'
import { KeyValueList } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { LoadingBlock } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import { defaultConfiguration } from './scalability'
import styles from './ScalabilityDialog.module.css'
import { SCALABILITY_FORM_ID, ScalabilityForm } from './ScalabilityForm'
import type { Target } from './target'

interface ScalabilityDialogProps {
  target: Target
  service: ServiceInfo
  onClose: () => void
}

export function ScalabilityDialog({ target, service, onClose }: ScalabilityDialogProps) {
  const { data, error, isLoading } = useScalability(target, service.name)
  const save = useSaveScalability()
  const toast = useToast()

  // A service that was never configured answers 404; the form then starts from an empty configuration.
  const notFound = error instanceof ApiError && error.status === 404
  const initial = data ?? (notFound ? defaultConfiguration(service.name, target) : undefined)

  const onSave = async (configuration: ScalabilityConfiguration) => {
    try {
      await save.mutateAsync({ ...target, service: service.name, configuration })
      toast.notify(`Autoscale configuration of ${service.name} saved`, 'success')
      onClose()
    } catch (err) {
      toast.error(err, 'Save failed')
    }
  }

  return (
    <Modal
      title={`${service.name} · autoscale configuration`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form={SCALABILITY_FORM_ID} disabled={!initial} loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <KeyValueList
        items={[
          { label: 'Service', value: service.name },
          { label: 'Resource group', value: service.resourceGroup ?? '–' },
          { label: 'Current instances', value: service.instanceCount },
        ]}
      />
      {notFound && <p className="u-muted">No autoscale configuration exists for this service yet. Saving creates one.</p>}
      <div className={styles.content}>
        {isLoading ? <LoadingBlock /> : initial ? <ScalabilityForm initial={initial} onSave={(configuration) => void onSave(configuration)} /> : <ErrorState error={error} />}
      </div>
    </Modal>
  )
}
